import HttpService from "./http-service";
import Video from "@/entities/video";
import AsyncPaginatedRequest from "./async-paginated-request";
import { LazyAsyncIter } from "@/iterators";
import { CamelCasedProperties, CamelCasedPropertiesDeep } from "type-fest";
import { pageMapperBuilder, Paginated } from "@/pagination";

export default class VideoService extends HttpService {
  public getAllVideos(
    params: SearchVideoRequestParams
  ): AsyncIterator<Paginated<Video>> {
    const asyncReq = new AsyncPaginatedRequest<VideoDTO>(
      "get",
      "videos",
      params,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();
    return new LazyAsyncIter(asyncReq).map(pageMapperBuilder(videoFromDTO));
  }

  public getAllPublicVideos(): AsyncIterator<Paginated<Video>> {
    const asyncReq = new AsyncPaginatedRequest<VideoDTO>(
      "get",
      "public-videos",
      undefined,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();
    return new LazyAsyncIter(asyncReq).map(pageMapperBuilder(videoFromDTO));
  }

  public getMainPageVideos(): AsyncIterator<
    Paginated<VideoWithMainPagePosition>
  > {
    const asyncReq = new AsyncPaginatedRequest<VideoWithMainPagePositionDTO>(
      "get",
      "public-videos",
      undefined,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();
    return new LazyAsyncIter(asyncReq).map(
      pageMapperBuilder(videoWithMainPagePositionFromDTO)
    );
  }

  public async getVideo(videoId: number): Promise<Video> {
    const { data } = await this.httpClient.get<VideoDTO>(`/videos/${videoId}`);
    return new Video(data);
  }

  public async getVideoMainPagePosition(
    videoId: number
  ): Promise<null | number> {
    const { data } = await this.httpClient.get<MainPageVideoDTO | null>(
      `/videos/${videoId}/main-page`
    );
    return data?.index ?? null;
  }

  public insertVideo(createVideo: CreateVideo): Promise<void> {
    const video: CreateVideo & CreateVideoDTO = createVideo;
    if (video.mainPagePosition != undefined) {
      video.main_page_video_index = video.mainPagePosition;
      video.mainPagePosition = undefined;
    }
    return this.httpClient.post(`/videos`, video);
  }

  public updateVideo(updateVideo: UpdateVideo): Promise<void> {
    const video: UpdateVideo & UpdateVideoDTO = updateVideo;
    if (updateVideo.mainPagePosition != undefined) {
      video.main_page_video_index = video.mainPagePosition;
      video.mainPagePosition = undefined;
    }
    return this.httpClient.put(`/videos/${video.id}`, video);
  }
  public deleteVideo(videoId: number): Promise<void> {
    return this.httpClient.delete(`/videos/${videoId}`);
  }
}

export type UpdateVideo = VideoDTO & { mainPagePosition?: undefined | number };
type UpdateVideoDTO = Omit<
  UpdateVideo & { main_page_video_index?: number },
  "id" | "mainPagePosition"
>;
export type CreateVideo = Omit<UpdateVideo, "id">;
type CreateVideoDTO = UpdateVideoDTO;

export interface MainPageVideoDTO {
  id: number;
  video_id: number;
  index: number;
}

export interface VideoDTO {
  id: number;
  title: string;
  description: string;
  url: string;
  status: number;
}

export function videoFromDTO(dto: VideoDTO): Video {
  return new Video({
    id: dto.id,
    title: dto.title,
    description: dto.description,
    url: dto.url,
    status: dto.status,
  });
}

export type VideoWithMainPagePositionDTO = VideoDTO & {
  main_page_video: MainPageVideoDTO;
};
export type VideoWithMainPagePosition =
  CamelCasedPropertiesDeep<VideoWithMainPagePositionDTO>;
type MainPageVideo = CamelCasedProperties<MainPageVideoDTO>;
export function mainPageVideoFromDTO(dto: MainPageVideoDTO): MainPageVideo {
  return {
    id: dto.id,
    videoId: dto.video_id,
    index: dto.index,
  };
}
export function videoWithMainPagePositionFromDTO(
  dto: VideoWithMainPagePositionDTO
): VideoWithMainPagePosition {
  return {
    ...videoFromDTO(dto),
    mainPageVideo: mainPageVideoFromDTO(dto.main_page_video),
  };
}

export interface SearchVideoRequestParams {
  title?: string;
  status?: string;
}
